import React, { useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "../../contexts/TranslationContext";
import useAnchoredPosition from "../../hooks/useAnchoredPosition";

// Native language names (autonyms) - the name of each language in that language
const NATIVE_LANGUAGE_NAMES = {
  'en': 'English',
  'es': 'Español',
  'fr': 'Français',
  'de': 'Deutsch',
  'it': 'Italiano',
  'pt': 'Português',
  'ru': 'Русский',
  'zh': '中文',
  'ja': '日本語',
  'ar': 'العربية',
  'hi': 'हिन्दी',
  'ko': '한국어',
  'hr': 'Hrvatski',
  'nl': 'Nederlands',
  'pl': 'Polski',
  'tr': 'Türkçe',
  'sv': 'Svenska',
  'da': 'Dansk',
  'no': 'Norsk',
  'fi': 'Suomi',
  'el': 'Ελληνικά',
  'cs': 'Čeština',
  'hu': 'Magyar',
  'ro': 'Română',
  'th': 'ไทย',
  'vi': 'Tiếng Việt',
  'id': 'Bahasa Indonesia',
  'ms': 'Bahasa Melayu',
  'uk': 'Українська',
  'he': 'עברית',
  'bn': 'বাংলা',
  'ta': 'தமிழ்',
  'te': 'తెలుగు',
  'mr': 'मराठी',
  'gu': 'ગુજરાતી',
  'kn': 'ಕನ್ನಡ',
  'ml': 'മലയാളം',
  'pa': 'ਪੰਜਾਬੀ',
  'ur': 'اردو',
  'fa': 'فارسی',
  'sk': 'Slovenčina',
  'bg': 'Български',
  'sr': 'Српски',
  'lt': 'Lietuvių',
  'lv': 'Latviešu',
  'et': 'Eesti',
  'sl': 'Slovenščina',
  'ca': 'Català',
  'eu': 'Euskara',
  'gl': 'Galego',
};

const LanguageSelector = ({ isFromAuth = false, compact = false }) => {
  const {
    currentLanguage,
    availableLanguages,
    isLoading,
    translationProgress,
    changeLanguage,
    clearCache,
    cacheSize,
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showCacheInfo, setShowCacheInfo] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const triggerRef = useRef(null);

  // D-003 — the 320px panel renders in a body portal, anchored to the trigger,
  // so it can never be clipped by an auth-page corner, a sidebar, or the mobile
  // menu, nor push the page sideways (A4). The compact header chip and the
  // top-right auth chip both sit near the right edge, so they anchor the panel
  // to their RIGHT edge (this preserves the PR #99 right-0 header behaviour);
  // the wider in-menu forms anchor left. All cases clamp to the viewport.
  const anchorRight = compact || isFromAuth;
  const { panelRef, style: menuStyle } = useAnchoredPosition(isOpen, triggerRef, {
    align: anchorRight ? "right" : "left",
    gap: 8,
    margin: 8,
  });

  const handleLanguageChange = async (languageCode) => {
    setIsOpen(false);
    setSearchQuery("");
    try {
      await changeLanguage(languageCode);
      // Force a small delay to ensure state updates propagate
      await new Promise(resolve => setTimeout(resolve, 50));
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const handleClearCache = () => {
    clearCache();
    setShowCacheInfo(false);
  };

  const currentLanguageData = useMemo(() => {
    if (!currentLanguage || !availableLanguages || availableLanguages.length === 0) {
      return { language: "en", name: "English", nativeName: "English" };
    }
    
    const normalizedCurrent = (currentLanguage || '').toLowerCase().trim();
    const found = availableLanguages.find(
      (lang) => {
        const langCode = (lang.language || '').toLowerCase().trim();
        return langCode === normalizedCurrent;
      }
    );
    
    // Get native name for the language
    const nativeName = NATIVE_LANGUAGE_NAMES[normalizedCurrent] || null;
    
    // If not found in availableLanguages, create a fallback
    if (!found) {
      const fallbackName = nativeName || normalizedCurrent.toUpperCase();
      return { language: normalizedCurrent, name: fallbackName, nativeName: fallbackName };
    }
    
    return { 
      ...found, 
      nativeName: nativeName || found.name 
    };
  }, [availableLanguages, currentLanguage]);

  // Filter languages based on search query
  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableLanguages;
    }

    const query = searchQuery.toLowerCase();
    return availableLanguages.filter(
      (lang) =>
        lang.name.toLowerCase().includes(query) ||
        lang.language.toLowerCase().includes(query)
    );
  }, [availableLanguages, searchQuery]);

  // Show popular languages first, then others
  const sortedLanguages = useMemo(() => {
    const popularCodes = [
      "EN",
      "ES",
      "FR",
      "DE",
      "IT",
      "PT",
      "RU",
      "ZH",
      "JA",
      "AR",
      "HI",
      "KO",
    ];
    const popular = [];
    const others = [];

    filteredLanguages.forEach((lang) => {
      if (popularCodes.includes(lang.language)) {
        popular.push(lang);
      } else {
        others.push(lang);
      }
    });

    // Sort popular languages by the order in popularCodes
    popular.sort(
      (a, b) =>
        popularCodes.indexOf(a.language) - popularCodes.indexOf(b.language)
    );
    // Sort others alphabetically
    others.sort((a, b) => a.name.localeCompare(b.name));

    return [...popular, ...others];
  }, [filteredLanguages]);

  if (availableLanguages.length === 0) {
    return null;
  }

  // Compact 2-letter code for the design's "EN" chip (falls back to the full
  // native name in the non-compact form used elsewhere, e.g. the mobile menu).
  const langCode = ((currentLanguage || "en").slice(0, 2) || "en").toUpperCase();

  return (
    <div className="relative" data-no-translate="true">
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={
          compact
            ? `flex items-center gap-1.5 h-[38px] px-2.5 rounded-[10px] bg-transparent hover:bg-[#F3F6FB] transition-colors relative flex-shrink-0 ${
                isLoading ? "opacity-75 cursor-not-allowed" : "cursor-pointer"
              }`
            : `flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 transition-colors duration-200 min-w-[140px] relative flex-shrink-0 ${
                isLoading ? "opacity-75 cursor-not-allowed" : "cursor-pointer"
              }`
        }
        data-no-translate="true"
        aria-label="Select language"
        title={`Current language: ${currentLanguageData.name}${
          cacheSize > 0 ? ` (${cacheSize} cached translations)` : ""
        }`}
      >
        {/* Language Icon (hidden in the compact chip) */}
        {!compact && (
          <svg
            className="w-4 h-4 text-gray-600 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
            />
          </svg>
        )}

        <span
          key={`lang-${currentLanguage}`}
          className={
            compact
              ? "text-[13.5px] font-semibold text-[#4B5563] flex-shrink-0"
              : "text-sm font-medium text-gray-700 flex-1 truncate min-w-0"
          }
          data-no-translate="true"
        >
          {compact ? langCode : currentLanguageData.nativeName}
        </span>

        {isLoading ? (
          <div className="relative">
            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
            {translationProgress > 0 && (
              <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
                {translationProgress}%
              </div>
            )}
          </div>
        ) : (
          <svg
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        )}

        {/* Progress bar overlay */}
        {isLoading && translationProgress > 0 && (
          <div
            className="absolute bottom-0 left-0 h-1 bg-blue-500 transition-all duration-300 rounded-b-lg"
            style={{ width: `${translationProgress}%` }}
          />
        )}
      </button>

      {isOpen && createPortal(
        <div
          ref={panelRef}
          style={menuStyle || { position: "fixed", top: 0, left: 0, visibility: "hidden" }}
          className="w-80 bg-white border border-gray-300 rounded-lg shadow-lg z-[9999] max-h-96 overflow-hidden"
        >
          {/* Search Input */}
          <div className="p-3 border-b bg-gray-50">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search languages..."
                className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <svg
                className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Cache Info Section */}
          {cacheSize > 0 && (
            <div className="px-4 py-2 border-b bg-gray-50 text-xs text-gray-600">
              <div className="flex items-center justify-between">
                <span>{cacheSize} translations cached</span>
                <button
                  onClick={() => setShowCacheInfo(!showCacheInfo)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {showCacheInfo ? "Hide" : "Info"}
                </button>
              </div>
              {showCacheInfo && (
                <div className="mt-2 pt-2 border-t space-y-1">
                  <p>
                    Translations are cached for 24 hours to improve performance.
                  </p>
                  <button
                    onClick={handleClearCache}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Clear Cache
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Language Options */}
          <div className="py-1 overflow-y-auto max-h-64">
            {sortedLanguages.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No languages found matching "{searchQuery}"
              </div>
            ) : (
              sortedLanguages.map((language, index) => {
                const isPopular = [
                  "EN",
                  "ES",
                  "FR",
                  "DE",
                  "IT",
                  "PT",
                  "RU",
                  "ZH",
                  "JA",
                  "AR",
                  "HI",
                  "KO",
                ].includes(language.language);
                const isFirstOther =
                  index > 0 &&
                  isPopular !==
                    [
                      "EN",
                      "ES",
                      "FR",
                      "DE",
                      "IT",
                      "PT",
                      "RU",
                      "ZH",
                      "JA",
                      "AR",
                      "HI",
                      "KO",
                    ].includes(sortedLanguages[index - 1].language);

                return (
                  <React.Fragment key={(language.code || language.language || language.name)}>
                    {isFirstOther && !searchQuery && (
                      <div className="px-4 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-t">
                        Other Languages
                      </div>
                    )}
                    <button
                      onClick={() => handleLanguageChange((language.language || '').toLowerCase())}
                      disabled={isLoading}
                      className={`
                        w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between
                        ${
                          (currentLanguage || '').toLowerCase() === (language.language || '').toLowerCase()
                            ? "bg-blue-50 text-blue-700 font-medium"
                            : "text-gray-700"
                        }
                        ${
                          isLoading
                            ? "opacity-50 cursor-not-allowed"
                            : "cursor-pointer"
                        }
                      `}
                    >
                      <div className="flex items-center space-x-2">
                        <span>{language.name}</span>
                        {isPopular && !searchQuery && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            Popular
                          </span>
                        )}
                      </div>
                      {(currentLanguage || '').toLowerCase() === (language.language || '').toLowerCase() && (
                        <svg
                          className="w-4 h-4 text-blue-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  </React.Fragment>
                );
              })
            )}
          </div>

          {/* Footer with tip */}
          <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                Translations persist across page navigation •{" "}
                {availableLanguages.length} languages available
              </span>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Overlay to close dropdown when clicking outside. Rendered in a portal
          too so it reliably sits under the portalled menu (z-[9999]) and above
          the page, catching outside clicks without a stacking-context tangle. */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => {
            setIsOpen(false);
            setSearchQuery("");
          }}
        />,
        document.body,
      )}
    </div>
  );
};

export default LanguageSelector;
